<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900 font-inter">reepo.io</h1>
            <p class="text-gray-600">Super Admin Dashboard</p>
          </div>
          <button @click="handleLogout" class="btn-tertiary">
            Sign Out
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.totalClients }}</div>
          <div class="text-gray-600">Total Clients</div>
        </div>
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.pendingQuotes }}</div>
          <div class="text-gray-600">Pending Quotes</div>
        </div>
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.activeShows }}</div>
          <div class="text-gray-600">Active Shows</div>
        </div>
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.totalLeads }}</div>
          <div class="text-gray-600">Total Leads</div>
        </div>
      </div>

      <!-- Quick Actions (muted link-cards) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <button
          @click="navigateTo('/admin/super/clients')"
          class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left"
        >
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">All Clients</h3>
              <p class="text-sm text-gray-600">Manage all platform clients</p>
            </div>
          </div>
        </button>

        <button
          @click="navigateTo('/admin/super/quotes')"
          class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left"
        >
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">Quote Requests</h3>
              <p class="text-sm text-gray-600">Review and approve quotes</p>
            </div>
          </div>
        </button>
      </div>

      <!-- Pending Quote Requests Section -->
      <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 mb-8">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-medium text-gray-900">Recent Quote Requests</h2>
            <button
              @click="navigateTo('/admin/super/quotes')"
              class="btn-secondary-sm text-sm px-4 py-2"
            >
              View All
            </button>
          </div>
        </div>
        <div class="p-6">
          <div v-if="loading" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-gray-600">Loading quote requests...</p>
          </div>
          <div v-else-if="quoteRequests.length === 0" class="text-center py-8">
            <p class="text-gray-600">No pending quote requests.</p>
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="request in quoteRequests"
              :key="request.id"
              class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="text-lg font-medium text-gray-900">{{ request.name }}</h3>
                  <p class="text-gray-600">{{ request.client_name }}</p>
                  <p class="text-sm text-gray-500">
                    {{ formatDate(request.start_date) }} - {{ formatDate(request.end_date) }} • 
                    {{ request.tablet_count }} tablets
                  </p>
                  <p class="text-sm text-gray-500">
                    Requested: {{ formatDateTime(request.quote_requested_at) }}
                  </p>
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="navigateTo(`/admin/super/quotes`)"
                    class="btn-primary text-sm px-4 py-2"
                  >
                    Review Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Client Activity -->
      <div class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-medium text-gray-900">Recent Client Activity</h2>
        </div>
        <div class="p-6">
          <div v-if="recentClients.length === 0" class="text-center py-8">
            <p class="text-gray-600">No recent client activity.</p>
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="client in recentClients"
              :key="client.id"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="font-medium text-gray-900">{{ client.company_name }}</h3>
                  <p class="text-gray-600">{{ client.email }}</p>
                  <p class="text-sm text-gray-500">
                    {{ client.total_shows }} shows • {{ client.total_leads }} leads
                  </p>
                </div>
                <div class="text-sm text-gray-500">
                  Joined: {{ formatDate(client.created_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import config from '../config.js'
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from '../utils/date.js'

// Router
const router = useRouter()

// Reactive data
const loading = ref(true)
const quoteRequests = ref([])
const recentClients = ref([])
const stats = reactive({
  totalClients: 0,
  pendingQuotes: 0,
  activeShows: 0,
  totalLeads: 0
})

// Methods
const loadDashboard = async () => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    // Load quote requests
    const quoteResponse = await fetch(`${config.apiBaseUrl}/admin/super-admin/quote-requests`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (quoteResponse.ok) {
      const quoteData = await quoteResponse.json()
      quoteRequests.value = quoteData.quote_requests.slice(0, 5) // Show only 5 most recent
      stats.pendingQuotes = quoteData.quote_requests.length
    }

    // Load clients
    const clientResponse = await fetch(`${config.apiBaseUrl}/admin/super-admin/clients`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (clientResponse.ok) {
      const clientData = await clientResponse.json()
      recentClients.value = clientData.clients.slice(0, 5) // Show only 5 most recent
      stats.totalClients = clientData.clients.length
    }

    // Load dashboard stats
    const statsResponse = await fetch(`${config.apiBaseUrl}/admin/super-admin/dashboard-stats`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      stats.activeShows = statsData.active_shows
      stats.totalLeads = statsData.total_leads
    }

  } catch (error) {
    console.error('Error loading super admin dashboard:', error)
    if (error.message.includes('401')) {
      router.push('/admin/login')
    }
  } finally {
    loading.value = false
  }
}

const navigateTo = (path) => {
  router.push(path)
}

const handleLogout = async () => {
  try {
    const token = localStorage.getItem('admin_token')
    if (token) {
      await fetch(`${config.apiBaseUrl}/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    }
  } catch (error) {
    console.error('Error during logout:', error)
  } finally {
    localStorage.removeItem('admin_token')
    router.push('/admin/login')
  }
}

const formatDate = (dateString) => fmtDate(dateString)
const formatDateTime = (dateString) => fmtDateTime(dateString)

// Lifecycle
onMounted(() => {
  loadDashboard()
})
</script> 